import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

/**
 * Landing page Cognito redirects to after a successful Managed Login.
 *
 * Amplify's signInWithRedirect() listens for the auth-code in the URL and
 * exchanges it for tokens automatically, but it does that on the page that
 * called it (here). We just need to wait for the AuthContext to pick up the
 * new session via the Hub event, then bounce to the dashboard.
 *
 * Cognito appends ?error=…&error_description=… on failure; in that case we
 * route back to /login which renders the message.
 */
export function AuthCallback() {
  const { user, loading, refresh } = useAuth()
  const navigate = useNavigate()
  const [waited, setWaited] = useState(0)

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search)
    if (sp.get('error') || sp.get('error_description')) {
      navigate(`/login?${sp.toString()}`, { replace: true })
      return
    }
    // Nudge AuthContext to re-check — Amplify processes the ?code= in the
    // URL on mount automatically; refresh() polls the session right after.
    void refresh()
  }, [navigate, refresh])

  // Once user is loaded, jump home. Belt-and-suspenders polling in case
  // Amplify's Hub event misses the first refresh cycle.
  useEffect(() => {
    if (!loading && user) {
      navigate('/', { replace: true })
      return
    }
    if (waited > 15) {
      // 15s × 500ms = 7.5s upper bound before we give up and surface an error.
      navigate('/login?error_description=Sign-in%20did%20not%20complete', { replace: true })
      return
    }
    const t = setTimeout(() => {
      setWaited((w) => w + 1)
      void refresh()
    }, 500)
    return () => clearTimeout(t)
  }, [loading, user, navigate, waited, refresh])

  return (
    <div className="grid h-screen supports-[height:100dvh]:h-dvh place-items-center bg-slate-50">
      <div className="text-center">
        <div className="penser-thinking mx-auto mb-3 inline-block">
          <img src="/penser-icon.png" alt="" width={40} height={40} />
        </div>
        <div className="text-sm text-slate-600">Completing sign-in…</div>
      </div>
    </div>
  )
}
